import {Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {PositionOfDialog} from '@domain/position-of-dialog';
import {BmpProductionScheduleItemInPathGroup} from '@domain/production-schedule/bmp-production-schedule-item-in-path-group';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionScheduleOrderLine} from '@domain/production-schedule/production-schedule-order-line';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {RepositionDialogComponent} from '@presentation/components/reposition-dialog/reposition-dialog.component';
import {OverviewListOrderLine} from '@presentation/pages/texfab/orderbook/overview/overview-list-order-line';
import {
  AssertionUtils,
  BaseComponent,
  ColDefBuilderFactoryService,
  DialogBuilderFactoryService,
  EmptyCellRendererComponent,
  GridOptionsBuilderFactoryService,
  NoDataOverlayComponentParams,
  OverlayComponentParams,
  StringUtils,
  TranslateService,
  Unit
} from '@vdw/angular-component-library';
import {AgGridAngular} from 'ag-grid-angular';
import {
  CellRendererSelectorResult,
  ColDef,
  GetRowIdParams,
  GridOptions,
  ICellRendererParams,
  IRowDragItem,
  IRowNode,
  ITooltipParams,
  RowDoubleClickedEvent,
  RowDragEndEvent,
  ValueGetterParams
} from 'ag-grid-community';
import {filter} from 'rxjs';
import {Cutbar} from '../../cutbar.enum';
import {EditProductionScheduleItemInPathGroupAction} from '../../modals/edit-production-schedule-item-in-path-group/edit-production-schedule-item-in-path-group-action';
import {EditProductionScheduleItemInPathGroupComponent} from '../../modals/edit-production-schedule-item-in-path-group/edit-production-schedule-item-in-path-group.component';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '../../production-schedule-plan';

@Component({
  selector: 'app-path-content-component',
  templateUrl: './path-content.component.html',
  styleUrls: ['./path-content.component.scss']
})
export class PathContentComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() public selectedPath: ProductionSchedulePath;
  @Input() public productionSchedule: ProductionSchedule;
  @Input() public productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet;
  @Input() public commercialUnit: Unit;
  @Input() public productionOrderCustomSettings: ProductionOrderCustomSettings;
  @Output() public selectedPathContentRowDataOutput = new EventEmitter<ProductionScheduleItemInPathGroup>();

  @ViewChild('pathContentGrid')
  public pathContentGrid: AgGridAngular;

  public gridOptions: GridOptions;
  public loadingForTheFirstTime = true;
  public selectedNodes: IRowNode[] = [];
  public selectedRowIndex: number | null = null;
  public selectedDragRowIndex: number | null = null;
  public selectedRowData: ProductionScheduleItemInPathGroup | null = null;
  public selectedDragRowData: ProductionScheduleItemInPathGroup | null = null;
  public listProductionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup[];

  public readonly GENERAL_UNIT_PICKS = 'GENERAL.UNIT.PICKS';

  private readonly booleanFalseTranslation = 'GENERAL.CONDITION.NO';
  private readonly booleanTrueTranslation = 'GENERAL.CONDITION.YES';

  public constructor(
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    private readonly translate: TranslateService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.initialiseGridOptions();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('selectedPath' in changes || 'productionSchedule' in changes) {
      this.updateListProductionScheduleItemInPathGroup();
    }

    if ('productionSchedule' in changes) {
      this.updateSelectedPath();
    }
  }

  public canShowHeader(): boolean {
    return this.selectedNodes.length === 0;
  }

  public canMovePath(up: boolean): boolean {
    if (this.selectedNodes.length > 1) {
      return false;
    }

    if (up) {
      return this.selectedRowIndex !== 0;
    } else {
      return this.selectedPath?.pathGroups?.length !== this.selectedRowIndex + 1;
    }
  }

  public canEditPath(): boolean {
    return this.selectedNodes?.length === 1;
  }

  public movePath(up: boolean): void {
    const newIndex = up ? this.selectedRowIndex - 1 : this.selectedRowIndex + 1;
    this.setNewRowIndex(newIndex, this.selectedRowIndex, this.selectedRowData);
  }

  public openEditProductionScheduleItemDialog(data?: ProductionScheduleItemInPathGroup): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .withWidth('334px')
      .withClass('reposition-dialog')
      .withCustomBackdrop('transparent-backdrop')
      .openDialog(RepositionDialogComponent, {
        component: EditProductionScheduleItemInPathGroupComponent,
        sourceElement: document.querySelector('.path-content-container .ag-checked input'),
        positionOfDialog: PositionOfDialog.LEFT,
        productionScheduleItemInPathGroup: AssertionUtils.isNullOrUndefined(data) ? this.selectedRowData : data,
        commercialUnit: this.commercialUnit,
        productionSchedule: this.productionSchedule,
        allowExceedingRequestedAmountOfOrderLineItems: this.productionOrderCustomSettings?.allowExceedingRequestedAmountOfOrderLineItems,
        pickDensityInPicksPerMeter: this.productionSchedule.machineQuality.pickDensity * 1000
      })
      .pipe(filter((result: {action: EditProductionScheduleItemInPathGroupAction; value: ProductionScheduleItemInPathGroup}) => !AssertionUtils.isNullOrUndefined(result)))
      .subscribe((result: {action: EditProductionScheduleItemInPathGroupAction; value: ProductionScheduleItemInPathGroup}) => {
        if (result.action === EditProductionScheduleItemInPathGroupAction.EDIT) {
          this.editProductionScheduleItem(result.value);
        } else {
          this.removeProductionScheduleItemInPathGroup();
        }
      });
  }

  public deletePath(): void {
    this.selectedNodes.forEach((selectedNode: IRowNode) => {
      const productionScheduleItemInPathGroup = selectedNode.data as ProductionScheduleItemInPathGroup;
      const productionSchedulePathOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(
        this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent
      );

      this.productionSchedulePlan.removeProductionScheduleItem(
        productionScheduleItemInPathGroup.uuid,
        this.selectedPath.pathNumber,
        productionSchedulePathOfColoredYarnSet.productionScheduleColoredYarnSet.startDent,
        true
      );
    });
  }

  public onRowDragEnter({node}: RowDragEndEvent<ProductionSchedulePath>): void {
    this.selectedDragRowIndex = node.rowIndex;
    this.pathContentGrid.api.forEachNode((rowNode: IRowNode, index: number) => {
      if (index === node.rowIndex) {
        this.selectedDragRowData = rowNode.data;
      }
    });

    document.querySelectorAll('.ag-dnd-ghost')[0].classList.add('ag-dnd-ghost--compact');
  }

  public onRowDragEnd({node}: RowDragEndEvent<ProductionSchedulePath>): void {
    this.setNewRowIndex(node.rowIndex, this.selectedDragRowIndex, this.selectedDragRowData);
  }

  private initialiseGridOptions(): void {
    this.gridOptions = this.gridOptionsBuilderFactoryService
      .getBuilder(this.getColumnDefsForListOfPathContent(), GridIdentifier.LIST_OF_PATH_CONTENTS)
      .withHeaderHeight(32)
      .withFloatingFiltersHeight(32)
      .withLoadingOverlay({
        scale: 0.4
      } as OverlayComponentParams)
      .withNoRowsOverlay({
        scale: 0.4,
        titleParam: 'PRODUCTION_ORDER.WEAVE_PLAN.PATH_CONTENT.PATH_CONTENT_SELECTED',
        descriptionKey: 'PRODUCTION_ORDER.WEAVE_PLAN.PATH_CONTENT.PATH_CONTENT_NO_ROWS_OVERLAY_DESCRIPTION'
      } as NoDataOverlayComponentParams)
      .withRowSelection(true)
      .withCompactView()
      .withPopupParent(document.querySelector('body'))
      .withGetRowId((params: GetRowIdParams): string => params.data.uuid)
      .withRowDraggingInsideGrid((params: IRowDragItem) => params.rowNode.data?.drawing.name)
      .withOnRowDataUpdated(() => {
        this.setPinnedColumn();
        this.setSelected();
      })
      .withOnRowDoubleClicked((event: RowDoubleClickedEvent) => {
        this.onRowDoubleClicked(event);
      })
      .withOnRowSelected(() => {
        this.onRowSelected();
      })
      .withoutSorting()
      .build();
  }

  private getColumnDefsForListOfPathContent(): ColDef[] {
    return [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('position')
        .withHeaderName('CUSTOMERS.NUMBER_ABBREVIATION')
        .withValueGetter((params: ValueGetterParams): number => params.node.rowIndex + 1)
        .withComparator(StringUtils.stringComparator)
        .withRightAlignment()
        .withLockPinned(true)
        .withSuppressMovable('left')
        .withMaxWidth(100)
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('drawingName')
        .withField('drawing.name')
        .withHeaderName('PRODUCTION_ORDER.CUSTOM_SETTINGS.PATH_LABELS.DRAWING_NAME')
        .withTooltipField('drawing.name')
        .withComparator(StringUtils.stringComparator)
        .withCellRendererSelector(this.cellRendererSelectorForOrderLinesOnly())
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('quantity')
        .withHeaderName('GENERAL.QUANTITY')
        .withValueGetter((params: ValueGetterParams) => {
          return this.productionSchedule.machine.jacquard.isSisal ? params.data.quantity : params.data.quantity * this.productionSchedule.getProductionScheduleItemMultiplier();
        }, true)
        .withComparator(StringUtils.stringComparator)
        .withRightAlignment()
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('cutbarBefore')
        .withCellRenderer((params: ICellRendererParams) => this.getTechnicalCutbarsLengthInNumberOfPicks(params.data, Cutbar.BEFORE))
        .withTooltipValueGetter((params: ITooltipParams) => this.getTechnicalCutbarsLengthInNumberOfPicks(params.data, Cutbar.BEFORE).toString())
        .withHeaderName(`CB (${this.translate.instant(`GENERAL.UNIT.PICKS`)})`)
        .withTooltipFieldForHeader('TEXTILE_DATA.FINISHING_TEMPLATE.CUTBAR.CUTBAR_BEFORE', this.GENERAL_UNIT_PICKS)
        .withComparator(StringUtils.stringComparator)
        .withRightAlignment()
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('cutbarAfter')
        .withCellRenderer((params: ICellRendererParams) => this.getTechnicalCutbarsLengthInNumberOfPicks(params.data, Cutbar.AFTER))
        .withTooltipValueGetter((params: ITooltipParams) => this.getTechnicalCutbarsLengthInNumberOfPicks(params.data, Cutbar.AFTER).toString())
        .withHeaderName(`CA (${this.translate.instant(`GENERAL.UNIT.PICKS`)})`)
        .withTooltipFieldForHeader('TEXTILE_DATA.FINISHING_TEMPLATE.CUTBAR.CUTBAR_AFTER', this.GENERAL_UNIT_PICKS)
        .withComparator(StringUtils.stringComparator)
        .withRightAlignment()
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('heightInPicks')
        .withField('technicalDimensions.heightInPicks', true)
        .withHeaderName(`P`)
        .withTooltipFieldForHeader('GENERAL.DIMENSIONS.HEIGHT', this.GENERAL_UNIT_PICKS)
        .withComparator(StringUtils.stringComparator)
        .withRightAlignment()
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('isProductionItem')
        .withHeaderName(`NP`)
        .withTooltipFieldForHeader('PRODUCTION_ORDER.WEAVE_PLAN.PATH_CONTENT.NO_PRODUCTION_ITEM')
        .withValueGetter(
          (params: ValueGetterParams) => (params.data.isProductionItem ? this.translate.instant(this.booleanTrueTranslation) : this.translate.instant(this.booleanFalseTranslation)),
          true
        )
        .withBooleanFilter(this.translate.instant(this.booleanFalseTranslation), this.translate.instant(this.booleanTrueTranslation))
        .withRightAlignment()
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('missingDesign')
        .withField('drawing.missingDesign')
        .withValueGetter(
          (params: ValueGetterParams) => (params.data.drawing.missingDesign ? this.translate.instant(this.booleanTrueTranslation) : this.translate.instant(this.booleanFalseTranslation)),
          true
        )
        .withHeaderName(`V`)
        .withTooltipFieldForHeader('ORDERBOOK.MISSING_DESIGN')
        .withBooleanFilter(this.translate.instant(this.booleanFalseTranslation), this.translate.instant(this.booleanTrueTranslation))
        .withRightAlignment()
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('sales')
        .withField('orderLineId')
        .withHeaderName('SALES.TITLE')
        .withValueGetter((params: ValueGetterParams) => this.getSalesOrderLineProperty(params.data.orderLineId, 'salesOrderReference'), true)
        .withComparator(StringUtils.stringComparator)
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('orderLine')
        .withField('orderLineId')
        .withHeaderName('OL')
        .withTooltipFieldForHeader('ORDERBOOK.ORDERLINE')
        .withValueGetter((params: ValueGetterParams) => this.getSalesOrderLineProperty(params.data.orderLineId, 'salesOrderLineReference'), true)
        .withComparator(StringUtils.stringComparator)
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('dueDate')
        .withValueGetter((params: ValueGetterParams) => this.productionSchedule.orderLines.find((orderline: ProductionScheduleOrderLine) => orderline.id === params.data.orderLineId)?.desiredDate)
        .withDateMultiFilter()
        .withHeaderName('GENERAL.DATETIME.DUE_DATE')
        .withDate()
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('widthInMM')
        .withField('commercialDimensionsInMM.widthInMM')
        .withMMConversion(Unit.CENTIMETER, false, 2)
        .withHeaderName(`X`)
        .withTooltipFieldForHeader('GENERAL.DIMENSIONS.WIDTH', 'GENERAL.UNIT.CENTIMETER')
        .withComparator(StringUtils.stringComparator)
        .withRightAlignment()
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('heightInMM')
        .withField('commercialDimensionsInMM.heightInMM')
        .withMMConversion(Unit.CENTIMETER, false, 2)
        .withHeaderName(`Y`)
        .withTooltipFieldForHeader('GENERAL.DIMENSIONS.HEIGHT', 'GENERAL.UNIT.CENTIMETER')
        .withComparator(StringUtils.stringComparator)
        .withRightAlignment()
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('finishingName')
        .withHeaderName(`TEXTILE_DATA.FINISHING.FINISHING`)
        .withValueGetter((params: ValueGetterParams<ProductionScheduleItemInPathGroup>) => this.getFinishingName(params.data))
        .withComparator(StringUtils.stringComparator)
        .withRightAlignment()
        .withoutFilter()
        .build()
    ];
  }

  private getTechnicalCutbarsLengthInNumberOfPicks(productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup, cutbar: Cutbar): number {
    const cutbarInformation = productionScheduleItemInPathGroup.getCutbarInformation();

    if (cutbarInformation) {
      if (cutbar === Cutbar.BEFORE && cutbarInformation.cutbarBefore) {
        return cutbarInformation.cutbarBefore.technicalLengthInNumberOfPicks || 0;
      } else if (cutbar === Cutbar.AFTER && cutbarInformation.cutbarAfter) {
        return cutbarInformation.cutbarAfter.technicalLengthInNumberOfPicks || 0;
      } else {
        return 0;
      }
    }

    return 0;
  }

  private getSalesOrderLineProperty(orderLineId: number, property: keyof OverviewListOrderLine): string {
    if (orderLineId !== null && this.productionSchedule && !AssertionUtils.isEmpty(this.productionSchedule.orderLines)) {
      const foundOrderLine = this.productionSchedule.orderLines.find((orderLine: ProductionScheduleOrderLine) => orderLine.id === orderLineId);

      if (foundOrderLine) {
        return foundOrderLine[property] as string;
      }
    }

    return null;
  }

  private cellRendererSelectorForOrderLinesOnly<TData = any, TValue = any>(
    cellRenderer: any = null,
    cellRendererParams: any = null
  ): (params: ICellRendererParams<TData, TValue>) => CellRendererSelectorResult {
    return (params: ICellRendererParams<TData, TValue>): CellRendererSelectorResult =>
      params.node.level === 0 ? {component: cellRenderer ?? params.colDef.cellRenderer, params: cellRendererParams} : {component: EmptyCellRendererComponent};
  }

  private getFinishingName(productionScheduleItem: ProductionScheduleItemInPathGroup): string {
    let name = '';

    if (!AssertionUtils.isNullOrUndefined(productionScheduleItem.orderline)) {
      name = productionScheduleItem.orderline?.article.finishing.nameWithVersion;
    } else if (!AssertionUtils.isNullOrUndefined(productionScheduleItem.drawing) && productionScheduleItem.drawing.drawingType === DrawingType.BMP) {
      const bmpProductionScheduleItem = productionScheduleItem as any as BmpProductionScheduleItemInPathGroup;
      name = bmpProductionScheduleItem?.finishing?.nameWithVersion;
    }

    return name;
  }

  private onRowSelected(): void {
    this.selectedNodes = this.pathContentGrid.api.getSelectedNodes();
    this.selectedRowIndex = this.selectedNodes.length > 0 ? this.selectedNodes[0].rowIndex : null;
    this.selectedRowData = this.selectedNodes.length > 0 ? this.selectedNodes[0].data : null;

    this.selectedPathContentRowDataOutput.emit(this.selectedNodes.length === 1 ? this.selectedRowData : null);
  }

  private onRowDoubleClicked(event: RowDoubleClickedEvent): void {
    if (event && event.node && event.node.data) {
      this.pathContentGrid.api.deselectAll();
      event.node.setSelected(true);
      this.onRowSelected();
      this.openEditProductionScheduleItemDialog(event.node.data);
    }
  }

  private updateListProductionScheduleItemInPathGroup(): void {
    const selectedPath = this.findSelectedPath();
    this.listProductionScheduleItemInPathGroup = AssertionUtils.isNullOrUndefined(selectedPath) ? [] : selectedPath.pathGroups;
  }

  private updateSelectedPath(): void {
    this.selectedPath = this.findSelectedPath();
  }

  private findSelectedPath(): ProductionSchedulePath {
    if (this.productionSchedule && this.selectedPath) {
      for (const coloredYarnSet of this.productionSchedule.productionSchedulePathsOfColoredYarnSets) {
        const foundPath = coloredYarnSet.productionSchedulePaths.find((path: ProductionSchedulePath) => path.pathNumber === this.selectedPath.pathNumber);

        if (!AssertionUtils.isNullOrUndefined(foundPath)) {
          return foundPath;
        }
      }
    }
  }

  private setNewRowIndex(index: number, selectedRowIndex: number, selectedRowData: ProductionScheduleItemInPathGroup): void {
    this.productionSchedulePlan.moveProductionScheduleItemToProductionSchedulePath(
      this.selectedPath.pathNumber,
      selectedRowIndex,
      this.productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet,
      this.selectedPath.pathNumber,
      index,
      this.productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet,
      selectedRowData
    );

    setTimeout(() => {
      this.selectedNodes = this.pathContentGrid.api.getSelectedNodes();
      this.selectedRowIndex = this.selectedNodes?.length > 0 ? this.selectedNodes[0].rowIndex : null;
    });
  }

  private editProductionScheduleItem(productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup): void {
    this.productionSchedulePlan.editProductionScheduleItem(
      productionScheduleItemInPathGroup,
      this.selectedRowIndex,
      this.getProductionSchedulePathIndex(),
      this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent
    );
  }

  private getProductionSchedulePathIndex(): number {
    return this.productionSchedule.productionSchedulePathsOfColoredYarnSets
      .find(
        (productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) =>
          productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent === this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent
      )
      .productionSchedulePaths.findIndex((productionSchedulePath: ProductionSchedulePath) => productionSchedulePath.pathNumber === this.selectedPath.pathNumber);
  }

  private removeProductionScheduleItemInPathGroup(): void {
    const productionSchedulePathOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(
      this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent
    );
    const productionSchedulePath = productionSchedulePathOfColoredYarnSet.productionSchedulePaths[this.selectedPath.pathNumber - 1];

    this.productionSchedulePlan.removeProductionScheduleItem(
      this.selectedRowData.uuid,
      productionSchedulePath.pathNumber,
      productionSchedulePathOfColoredYarnSet.productionScheduleColoredYarnSet.startDent,
      true
    );
  }

  private setPinnedColumn(): void {
    this.pathContentGrid.api.setColumnsPinned(['position'], 'left');
  }

  private setSelected(): void {
    if (!AssertionUtils.isNullOrUndefined(this.selectedRowData)) {
      const selectedNode = this.pathContentGrid.api.getRenderedNodes().find((rowNode: IRowNode) => rowNode.data.uuid === this.selectedRowData.uuid);

      if (!AssertionUtils.isNullOrUndefined(selectedNode)) {
        this.selectedRowData = selectedNode.data;
      }
    }
  }
}
